import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import { Warning as WarningIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

interface DangerZoneProps {
  siteId: string;
  siteName: string;
  siteRoute: string;
}

function DangerZone({ siteId, siteName, siteRoute }: DangerZoneProps) {
  const navigate = useNavigate();
  const [firstDialogOpen, setFirstDialogOpen] = useState(false);
  const [secondDialogOpen, setSecondDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFirstConfirm = () => {
    setFirstDialogOpen(false);
    setSecondDialogOpen(true);
  };

  const handleDelete = async () => {
    if (confirmText !== siteName) {
      setError('群組名稱不正確');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/sites/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Close dialog and navigate to home
      setSecondDialogOpen(false);
      navigate('/');
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '刪除群組失敗');
      } else {
        setError('刪除群組時發生錯誤');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ px: 3 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          px: 3,
          py: 3, 
          border: '2px solid',
          borderColor: 'error.main',
          borderRadius: 2,
          bgcolor: 'error.lighter'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarningIcon sx={{ color: 'error.main', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
            危險區域
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          刪除群組是永久性操作，無法復原。所有相關的活動、報名記錄和設定都將被刪除。
        </Typography>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setFirstDialogOpen(true)}
          sx={{ fontWeight: 'bold' }}
        >
          刪除群組
        </Button>
      </Paper>

      {/* First Confirmation Dialog */}
      <Dialog
        open={firstDialogOpen}
        onClose={() => setFirstDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ color: 'error.main', mr: 1 }} />
          確認刪除群組？
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            您即將刪除群組「<strong>{siteName}</strong>」。
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            此操作將會：
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>永久刪除所有活動和報名記錄</li>
            <li>移除所有群組設定</li>
            <li>無法恢復任何資料</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            請確保您已經備份所有重要資料
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFirstDialogOpen(false)}>
            取消
          </Button>
          <Button 
            onClick={handleFirstConfirm} 
            color="error" 
            variant="contained"
          >
            繼續
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second Confirmation Dialog */}
      <Dialog
        open={secondDialogOpen}
        onClose={() => !isDeleting && setSecondDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          最後確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            這是最後一次確認。請輸入群組名稱「<strong>{siteName}</strong>」以確認刪除。
          </DialogContentText>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="輸入群組名稱以確認"
            value={confirmText}
            onChange={(e) => {
              setConfirmText(e.target.value);
              setError('');
            }}
            disabled={isDeleting}
            error={!!error}
            autoFocus
            sx={{ mt: 2 }}
          />

          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>警告：</strong>此操作無法復原！
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setSecondDialogOpen(false);
              setConfirmText('');
              setError('');
            }}
            disabled={isDeleting}
          >
            取消
          </Button>
          <Button 
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting || confirmText !== siteName}
            startIcon={isDeleting ? null : <DeleteIcon />}
          >
            {isDeleting ? '刪除中...' : '永久刪除群組'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DangerZone;
